import { useEffect, useRef, useState, useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import mermaid from 'mermaid';
import DOMPurify from 'dompurify';

let mermaidInitialized = false;

function initMermaid() {
  if (mermaidInitialized) return;
  mermaid.initialize({
    startOnLoad: false,
    theme: 'default',
    securityLevel: 'strict',
  });
  mermaidInitialized = true;
}

let idCounter = 0;

const MIN_SCALE = 0.5;
const MAX_SCALE = 5;

function getTouchDistance(t0: React.Touch, t1: React.Touch): number {
  const dx = t0.clientX - t1.clientX;
  const dy = t0.clientY - t1.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

export default function MermaidBlock({ code }: { code: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string>('');
  const idRef = useRef(`mermaid-${++idCounter}`);

  const [interactive, setInteractive] = useState(false);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const isPanning = useRef(false);
  const lastPointer = useRef({ x: 0, y: 0 });
  const pinchStartDist = useRef(0);
  const pinchStartScale = useRef(1);

  useEffect(() => {
    initMermaid();
    let cancelled = false;
    mermaid.render(idRef.current, code).then(
      ({ svg: rendered }) => {
        if (!cancelled) setSvg(rendered);
      },
      (err) => {
        if (!cancelled) setError(String(err));
      },
    );
    return () => { cancelled = true; };
  }, [code]);

  // Wheel zoom (native listener to support preventDefault with passive: false)
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !interactive) return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      setScale((prev) => {
        const factor = e.deltaY > 0 ? 0.9 : 1.1;
        return Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev * factor));
      });
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [interactive]);


  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!interactive) return;
    isPanning.current = true;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }, [interactive]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!interactive || !isPanning.current) return;
    const dx = e.clientX - lastPointer.current.x;
    const dy = e.clientY - lastPointer.current.y;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    setTranslate((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
  }, [interactive]);

  const handlePointerUp = useCallback((_e: React.PointerEvent) => {
    isPanning.current = false;
  }, []);


  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!interactive) return;
    if (e.touches.length === 2) {
      pinchStartDist.current = getTouchDistance(e.touches[0], e.touches[1]);
      pinchStartScale.current = scale;
    }
  }, [interactive, scale]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!interactive) return;
    if (e.touches.length === 2) {
      const dist = getTouchDistance(e.touches[0], e.touches[1]);
      const ratio = dist / pinchStartDist.current;
      const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, pinchStartScale.current * ratio));
      setScale(newScale);
    }
  }, [interactive]);

  const handleTouchEnd = useCallback((_e: React.TouchEvent) => {
    pinchStartDist.current = 0;
    pinchStartScale.current = 1;
  }, []);


  const resetView = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const toggleInteractive = useCallback(() => {
    setInteractive((prev) => {
      if (prev) {
        // Exiting interactive mode - reset view
        resetView();
      }
      return !prev;
    });
  }, [resetView]);

  // Escape key listener
  useEffect(() => {
    if (!interactive) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        resetView();
        setInteractive(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [interactive, resetView]);

  if (error) {
    return (
      <Box component="pre" sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1, overflow: 'auto', color: 'error.main', fontSize: '0.875em' }}>
        <code>{code}</code>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', my: 1 }}>
      {/* Control buttons */}
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, display: 'flex', gap: 0.5 }}>
        {interactive && (
          <IconButton
            size="small"
            onClick={resetView}
            title="Reset view"
            sx={{ bgcolor: 'grey.700', color: 'common.white', '&:hover': { bgcolor: 'grey.900' } }}
          >
            <RestartAltIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={toggleInteractive}
          title={interactive ? 'Exit pan/zoom' : 'Enable pan/zoom'}
          sx={{ bgcolor: 'grey.700', color: 'common.white', '&:hover': { bgcolor: 'grey.900' } }}
        >
          {interactive ? <CloseIcon fontSize="small" /> : <ZoomInIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Chart container */}
      <Box
        ref={containerRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        sx={{
          position: 'relative',
          zIndex: 0,
          overflow: interactive ? 'hidden' : 'auto',
          bgcolor: '#fff',
          borderRadius: 1,
          p: 1,
          ...(interactive && {
            cursor: 'grab',
            border: '2px solid',
            borderColor: 'primary.main',
            touchAction: 'none',
            userSelect: 'none',
          }),
          '& svg': {
            ...(interactive
              ? {
                  height: 'auto',
                  display: 'block',
                  transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
                  transformOrigin: 'center center',
                }
              : {
                  maxWidth: '100%',
                  height: 'auto',
                  display: 'block',
                }),
          },
        }}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true } }) }}
      />
    </Box>
  );
}
